import React, { useState, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Tooltip
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { auth } from '../firebase';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker - using unpkg for reliability
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

function PDFViewer({ documentUrl, documentName, annotations = [], initialPage = 1, onPageChange }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(error) {
    console.error('Error loading PDF:', error);
    setError(error.message || 'Failed to load PDF');
    setLoading(false);
  }

  // Convert relative URL to absolute if needed
  const getAbsoluteUrl = (url) => {
    if (!url) return null;
    
    // If it's already an absolute URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it's a relative URL, prepend the API base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';
    return `${apiBaseUrl}${url}`;
  };

  const absoluteUrl = getAbsoluteUrl(documentUrl);
  const [pdfFile, setPdfFile] = useState(null);

  // Memoize options to prevent unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
    cMapPacked: true,
  }), []);

  // Fetch PDF with authentication
  useEffect(() => {
    const fetchPDF = async () => {
      if (!absoluteUrl) return;
      
      try {
        const user = auth.currentUser;
        if (!user) {
          setError('Not authenticated');
          return;
        }

        const token = await user.getIdToken();
        
        // Fetch with authorization header
        const response = await fetch(absoluteUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfFile(url);
      } catch (err) {
        console.error('Error fetching PDF:', err);
        setError(err.message || 'Failed to load PDF');
        setLoading(false);
      }
    };

    fetchPDF();

    // Cleanup - don't revoke immediately as PDF.js needs time to load
    return () => {
      if (pdfFile) {
        // Delay cleanup to allow PDF.js to finish loading
        setTimeout(() => {
          URL.revokeObjectURL(pdfFile);
        }, 1000);
      }
    };
  }, [absoluteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update page when initialPage prop changes
  useEffect(() => {
    if (initialPage && initialPage !== pageNumber) {
      setPageNumber(initialPage);
    }
  }, [initialPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1;
      setPageNumber(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1;
      setPageNumber(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  // Navigate to previous annotation
  const goToPrevAnnotation = () => {
    const currentIndex = annotations.findIndex(ann => ann.page === pageNumber);
    if (currentIndex > 0) {
      setPageNumber(annotations[currentIndex - 1].page);
    } else if (currentIndex === -1 && annotations.length > 0) {
      // If not on an annotation page, go to last annotation
      setPageNumber(annotations[annotations.length - 1].page);
    }
  };

  // Navigate to next annotation
  const goToNextAnnotation = () => {
    const currentIndex = annotations.findIndex(ann => ann.page === pageNumber);
    if (currentIndex !== -1 && currentIndex < annotations.length - 1) {
      setPageNumber(annotations[currentIndex + 1].page);
    } else if (currentIndex === -1 && annotations.length > 0) {
      // If not on an annotation page, go to first annotation
      setPageNumber(annotations[0].page);
    }
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

  // Filter annotations for current page
  const currentPageAnnotations = annotations.filter(
    ann => ann.page === pageNumber
  );

  // Get current annotation index
  const currentAnnotationIndex = annotations.findIndex(ann => ann.page === pageNumber);
  const hasAnnotations = annotations.length > 0;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} noWrap>
            {documentName}
          </Typography>
          
          {/* Zoom Controls */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={zoomOut} disabled={scale <= 0.5}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Chip label={`${Math.round(scale * 100)}%`} size="small" />
            <IconButton size="small" onClick={zoomIn} disabled={scale >= 2.0}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* PDF Viewer */}
        <Box 
          sx={{ 
            flex: 1, 
            overflow: 'auto', 
            bgcolor: 'grey.100', 
            borderRadius: 1,
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 2
          }}
        >
          {error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="body2">
                Failed to load PDF: {error}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                URL: {documentUrl}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography>Loading PDF...</Typography>
                  </Box>
                }
                options={pdfOptions}
              >
                <Page 
                  pageNumber={pageNumber} 
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  loading={
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption">Loading page...</Typography>
                    </Box>
                  }
                />
              </Document>

              {/* Annotations Overlay */}
              {currentPageAnnotations.map((annotation, idx) => (
              <Tooltip 
                key={idx}
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {annotation.title}
                    </Typography>
                    <Typography variant="caption">
                      {annotation.text}
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <Paper
                  sx={{
                    position: 'absolute',
                    top: `${annotation.y || 10}%`,
                    left: `${annotation.x || 80}%`,
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    boxShadow: 3,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s',
                    zIndex: 10
                  }}
                >
                  📍 {idx + 1}
                </Paper>
              </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        {/* Annotations List */}
        {currentPageAnnotations.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: 150, overflow: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Annotations on this page:
            </Typography>
            {currentPageAnnotations.map((annotation, idx) => (
              <Paper key={idx} sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {annotation.title}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {annotation.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Annotation Navigation */}
        {hasAnnotations && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<NavigateBeforeIcon />}
              onClick={goToPrevAnnotation}
              disabled={currentAnnotationIndex <= 0}
              color="primary"
            >
              Prev Finding
            </Button>
            <Chip 
              label={`Finding ${currentAnnotationIndex + 1} of ${annotations.length}`}
              size="small"
              color="primary"
            />
            <Button
              variant="contained"
              size="small"
              endIcon={<NavigateNextIcon />}
              onClick={goToNextAnnotation}
              disabled={currentAnnotationIndex === -1 || currentAnnotationIndex >= annotations.length - 1}
              color="primary"
            >
              Next Finding
            </Button>
          </Box>
        )}

        {/* Page Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<NavigateBeforeIcon />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            Previous
          </Button>
          
          <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
            Page {pageNumber} of {numPages || '?'}
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            endIcon={<NavigateNextIcon />}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          >
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default PDFViewer;
